import React, { useState, useEffect } from "react";
import {
  Avatar,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  Favorite,
  FavoriteBorder,
  MoreVert,
  Reply,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { getTimeAgo } from "./utils";
import { CommentType } from "./IcommentType";



export const ReplyItem: React.FC<{
  reply: CommentType;
  onAddReply: (parentId: number, text: string) => void;
  onEditReply: (id: number, text: string) => void;
  onDeleteReply: (id: number) => void;
  onLikeReply: (commentId: number) => void; 
  depth?: number; // Add depth prop to control indentation
}> = ({
  reply,
  onAddReply,
  onEditReply,
  onDeleteReply,
  onLikeReply,
  depth = 0,
}) => {
  const [timeAgo, setTimeAgo] = useState(
    getTimeAgo(new Date(reply.created_on))
  );
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(reply.comment);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [showNestedReplies, setShowNestedReplies] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeAgo(getTimeAgo(new Date(reply.created_on)));
    }, 60000);

    return () => clearInterval(interval);
  }, [reply.created_on]);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    setEditText(reply.comment);
    setIsEditing(true);
    handleMenuClose();
  };

  const handleSaveEdit = () => {
    onEditReply(reply.id, editText);
    setIsEditing(false);
  };

  const handleDeleteClick = () => {
    onDeleteReply(reply.id);
    handleMenuClose();
  };

  const handleAddReplyClick = () => {
    if (replyText.trim()) {
      onAddReply(reply.id, replyText);
      setReplyText("");
      setShowReplyInput(false);
      // Auto-expand nested replies when adding a new one
      if (reply.children.length === 0) {
        setShowNestedReplies(true);
      }
    }
  };

  const toggleNestedReplies = () => {
    setShowNestedReplies(!showNestedReplies);
  };

  return (
    <Box
      sx={{
        mb: 2,
        pl: Math.min(depth * 2, 8), // Limit maximum indentation
        "& .MuiButton-root": {
          minWidth: "auto",
        },
        "& .MuiIconButton-root": {
          padding: "4px",
        },
      }}
    >
      <Box display="flex" gap={2}>
     <Avatar
            sx={{
              bgcolor: reply.is_user_commented ? "#1976d2" : "#ccc",
              width: 32,
              height: 32,
              // color: comment.is_user_commented ? "#ffff" : "#0000",
              fontSize: "16px",
              lineHeight: "24px",
              fontWeight: "400",
            }}
          >
            {reply.alice_name?.toLocaleUpperCase()}
          </Avatar>
        <Box flexGrow={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontWeight="500" fontSize="13px">
              {reply.is_user_commented === true ? "You" : `${reply.student_name}`}
            </Typography>
            <Typography color="text.secondary" fontSize="10px">
              {timeAgo}
            </Typography>
            {reply.is_user_commented && (
              <>
                <IconButton
                  size="small"
                  onClick={handleMenuClick}
                  sx={{ ml: "auto" }}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  <MenuItem onClick={handleEditClick}>Edit</MenuItem>
                  <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
                </Menu>
              </>
            )}
          </Box>

          {isEditing ? (
            <Box mt={1}>
              <TextField
                fullWidth
                multiline
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                size="small"
              />
              <Box mt={1} display="flex" gap={1}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSaveEdit}
                >
                  Save
                </Button>
                <Button size="small" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography
              mt={1}
              sx={{ whiteSpace: "pre-wrap", fontSize: "13px" }}
            >
              {reply.comment}
            </Typography>
          )}

          <Box display="flex" alignItems="center" gap={1} mt={1}>
               <Typography
              className="like-button-main"
              onClick={() => onLikeReply(reply.id)}
            >
              <IconButton
                size="small"
                // onClick={() =>}
                sx={{
                  color: reply.is_user_liked ? "#ef4444":"text.secondary",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
                aria-label="like"
                className="like-icon"
              >
                {reply.is_user_liked ? (
                  <Favorite fontSize="small" />
                ) : (
                  <FavoriteBorder fontSize="small" />
                )}
              </IconButton>
              <Typography
                variant="caption"
                color={reply.likes_count === 0 ? "text.secondary" : "#ef4444"}
                fontSize={"14px"}
                className="like-button"
              >
                {reply.likes_count}
              </Typography>
            </Typography>

            <Button
              size="small"
              startIcon={<Reply fontSize="small" />}
              onClick={() => setShowReplyInput(!showReplyInput)}
            >
              Reply
            </Button>

            {/* Show nested replies toggle button */}
            {reply.children.length > 0 && (
              <Button
                size="small"
                startIcon={
                  showNestedReplies ? (
                    <KeyboardArrowUp />
                  ) : (
                    <KeyboardArrowDown />
                  )
                }
                onClick={toggleNestedReplies}
              >
                {showNestedReplies
                  ? "Hide Replies"
                  : `Show Replies (${reply.children.length})`}
              </Button>
            )}
          </Box>

          {showReplyInput && (
            <Box mt={1}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                placeholder="Write a reply..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
              />
              <Box mt={1} display="flex" gap={1}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleAddReplyClick}
                  disabled={!replyText.trim()}
                >
                  Post Reply
                </Button>
                <Button size="small" onClick={() => setShowReplyInput(false)}>
                  Cancel
                </Button>
              </Box>
            </Box>
          )}

          {/* Nested replies rendering */}
          <Collapse in={showNestedReplies}>
            <Box sx={{ mt: 2 }}>
              {reply.children.map((nestedReply) => (
                <ReplyItem
                  key={nestedReply.id}
                  reply={nestedReply}
                  onAddReply={onAddReply}
                  onEditReply={onEditReply}
                  onDeleteReply={onDeleteReply}
                  onLikeReply={onLikeReply}
                  depth={depth + 1} // Increment depth for nested replies
                />
              ))}
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
};
